import { useMemo, useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Archive, ArrowLeft, Copy, FileText, Loader2, Plus, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import { Input } from '@/components/atoms/input';
import { Skeleton } from '@/components/atoms/skeleton';
import { Switch } from '@/components/atoms/switch';
import { Textarea } from '@/components/atoms/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import {
  useArchiveReportTemplate,
  useCloneReportTemplate,
  useCreateReportTemplate,
  useCreateTemplateSection,
  useDeleteTemplateSection,
  usePublishReportTemplate,
  useReorderTemplateSections,
  useReportTemplate,
  useReportTemplates,
  useUpdateReportTemplate,
  useUpdateTemplateSection,
  type ReportTemplateSummary,
  type UpdateReportTemplateInput,
} from '@/hooks/reports/useReportTemplates';
import { useReportFieldCatalog } from '@/hooks/reports/useReports';
import {
  ReportDocEditor,
  type SectionSaveStatus,
} from '@/features/reports/doc-editor/ReportDocEditor';
import { SectionSidebar } from '@/features/reports/doc-editor/SectionSidebar';
import {
  ReportDocEditorContext,
  type ReportDocEditorEnv,
  type ReportDocPhoto,
} from '@/features/reports/doc-editor/context';

// Template authoring (doc-editor redo plan §5/§6) — a template is simply a
// pre-authored document: the same section list + TipTap editor as a report,
// in template mode (no photo uploads — template content must not reference
// photoIds; grid/before-after placeholders ship empty), plus the workflow
// settings pane (§9 resolved decisions: two toggles — office review +
// customer signature).

export const Route = createFileRoute('/_dashboard/dashboard/settings/reports_/templates')({
  component: ReportTemplatesPage,
  validateSearch: (search): { template?: string } => ({
    template: typeof search.template === 'string' ? search.template : undefined,
  }),
});

const DEFAULT_SIGNATURE_LABEL = 'I acknowledge the findings above';

function ReportTemplatesPage() {
  const { template } = Route.useSearch();
  if (template) return <TemplateEditor templateId={template} />;
  return <TemplatesList />;
}

// ─── List ──────────────────────────────────────────────────────────────────

function TemplatesList() {
  const navigate = useNavigate();
  const { data, isLoading } = useReportTemplates();
  const list: ReportTemplateSummary[] = data ?? [];
  const create = useCreateReportTemplate();

  const openTemplate = (id: string) => {
    void navigate({
      to: '/dashboard/settings/reports/templates',
      search: { template: id },
    });
  };

  const handleCreate = async () => {
    try {
      const tpl = await create.mutateAsync({ name: 'Untitled template' });
      openTemplate(tpl.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create template');
    }
  };

  return (
    <div className="h-full max-w-5xl mx-auto overflow-y-auto p-6">
      <header className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6" /> Report Templates
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Templates belong to this account and can be customized for its reporting workflows.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleCreate} disabled={create.isPending}>
            {create.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            New template
          </Button>
          <Link to="/dashboard/settings/reports">
            <Button variant="outline">Back to settings</Button>
          </Link>
        </div>
      </header>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="border rounded-lg p-12 text-center">
          <p className="text-muted-foreground">No report templates yet for this account.</p>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sections</TableHead>
                <TableHead>Service types</TableHead>
                <TableHead>Recommendation</TableHead>
                <TableHead className="w-[150px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((tpl) => (
                <TemplateRow key={tpl.id} tpl={tpl} onOpen={openTemplate} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function TemplateStatusBadge({ status }: { status: ReportTemplateSummary['status'] }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
        status === 'PUBLISHED'
          ? 'bg-emerald-100 text-emerald-700'
          : status === 'DRAFT'
            ? 'bg-amber-100 text-amber-700'
            : 'bg-stone-100 text-stone-500'
      }`}
    >
      {status}
    </span>
  );
}

function TemplateRow({
  tpl,
  onOpen,
}: {
  tpl: ReportTemplateSummary;
  onOpen: (id: string) => void;
}) {
  const clone = useCloneReportTemplate(tpl.id);

  const handleClone = async () => {
    try {
      const copy = await clone.mutateAsync();
      onOpen(copy.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not clone template');
    }
  };

  return (
    <TableRow>
      <TableCell className="font-medium">
        <button type="button" onClick={() => onOpen(tpl.id)} className="hover:underline">
          {tpl.name}
        </button>
      </TableCell>
      <TableCell>
        <TemplateStatusBadge status={tpl.status} />
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{tpl.sections.length}</TableCell>
      <TableCell className="text-sm whitespace-normal wrap-anywhere">
        {tpl.serviceTypes.length === 0 ? '—' : tpl.serviceTypes.join(', ')}
      </TableCell>
      <TableCell className="text-sm">{tpl.allowEstimateSpawn ? 'Yes' : 'No'}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => onOpen(tpl.id)}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={handleClone} disabled={clone.isPending}>
            <Copy className="h-3.5 w-3.5 mr-1" />
            Clone
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

// ─── Editor ────────────────────────────────────────────────────────────────

function sectionAnchorId(sectionId: string): string {
  return `template-section-${sectionId}`;
}

function TemplateEditor({ templateId }: { templateId: string }) {
  const navigate = useNavigate();
  const { data: tpl, isLoading } = useReportTemplate(templateId);
  const fieldCatalog = useReportFieldCatalog();
  const update = useUpdateReportTemplate(templateId);
  const publish = usePublishReportTemplate(templateId);
  const archive = useArchiveReportTemplate(templateId);
  const clone = useCloneReportTemplate(templateId);
  const createSection = useCreateTemplateSection(templateId);
  const updateSection = useUpdateTemplateSection(templateId);
  const deleteSection = useDeleteTemplateSection(templateId);
  const reorderSections = useReorderTemplateSections(templateId);
  const [statuses, setStatuses] = useState<Record<string, SectionSaveStatus>>({});
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const flushersRef = useRef(new Map<string, () => Promise<void>>());

  // Persist each section editor's pending (debounced) edits before publishing
  // so the published template can't lock in stale content.
  const flushAllEditors = async () => {
    await Promise.all(Array.from(flushersRef.current.values()).map((flush) => flush()));
  };

  const contentEditable = tpl?.status !== 'ARCHIVED';
  const metaEditable = tpl?.status !== 'ARCHIVED';

  const orderedSections = useMemo(
    () => [...(tpl?.sections ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [tpl?.sections],
  );

  // Template mode env — no photos exist on a template, and the backend
  // rejects template content referencing photoIds, so uploads are disabled.
  const env = useMemo<ReportDocEditorEnv>(
    () => ({
      reportId: templateId,
      readOnly: !contentEditable,
      photoUploadsEnabled: false,
      // The catalog carries no values here: a template has no report behind
      // it, so every field shows its label until a report is built from it.
      fields: fieldCatalog.data ?? [],
      fieldsHaveValues: false,
      photosById: new Map<string, ReportDocPhoto>(),
      uploadPhoto: async () => {
        throw new Error('Photos are added on the report, not the template');
      },
      updatePhoto: async () => undefined,
    }),
    [templateId, contentEditable, fieldCatalog.data],
  );

  const goBack = () => {
    void navigate({
      to: '/dashboard/settings/reports/templates',
      search: { template: undefined },
    });
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-4 p-6">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (!tpl) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Template not found.</p>
        <Button variant="outline" className="mt-3" onClick={goBack}>
          <ArrowLeft className="h-4 w-4" />
          Back to templates
        </Button>
      </div>
    );
  }

  const patch = async (input: Parameters<typeof update.mutateAsync>[0]) => {
    try {
      await update.mutateAsync(input);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save template');
    }
  };

  const onPublish = async () => {
    try {
      await flushAllEditors();
      await publish.mutateAsync();
      toast.success('Template published — it can now be used on reports');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not publish template');
    }
  };

  const onArchive = async () => {
    try {
      await archive.mutateAsync();
      toast.success('Template archived');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not archive template');
    }
  };

  const onClone = async () => {
    try {
      const copy = await clone.mutateAsync();
      toast.success('Template cloned');
      void navigate({
        to: '/dashboard/settings/reports/templates',
        search: { template: copy.id },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not clone template');
    }
  };

  // ── Section CRUD ──────────────────────────────────────────────────────
  const onSelectSection = (sectionId: string) => {
    setActiveSectionId(sectionId);
    document
      .getElementById(sectionAnchorId(sectionId))
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onAddSection = async (title: string) => {
    try {
      await createSection.mutateAsync({ title });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not add section');
    }
  };

  const onRenameSection = async (sectionId: string, title: string) => {
    try {
      await updateSection.mutateAsync({ sectionId, title });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not rename section');
    }
  };

  const onDeleteSection = async (sectionId: string) => {
    const section = orderedSections.find((s) => s.id === sectionId);
    if (
      !window.confirm(
        `Delete section "${section?.title ?? 'Untitled'}"? Its content is removed from the template.`,
      )
    ) {
      return;
    }
    try {
      await deleteSection.mutateAsync(sectionId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete section');
    }
  };

  const onSectionFlagChange = async (
    sectionId: string,
    flag: 'customerVisible' | 'pageBreakAfter' | 'hiddenWhenEmpty',
    value: boolean,
  ) => {
    try {
      await updateSection.mutateAsync({ sectionId, [flag]: value });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update section');
    }
  };

  return (
    <div className="mx-auto h-full max-w-7xl overflow-y-auto p-6">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={goBack}
            className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" />
            All templates
          </button>
          {metaEditable ? (
            <Input
              key={tpl.id}
              defaultValue={tpl.name}
              onBlur={(e) => {
                const name = e.target.value.trim();
                if (name && name !== tpl.name) void patch({ name });
              }}
              className="max-w-md text-xl font-bold"
              aria-label="Template name"
            />
          ) : (
            <h1 className="truncate text-2xl font-bold">{tpl.name}</h1>
          )}
          <div className="mt-2 flex items-center gap-2">
            <TemplateStatusBadge status={tpl.status} />
            <span className="text-xs text-muted-foreground">v{tpl.version}</span>
            {tpl.status === 'PUBLISHED' ? (
              <span className="text-xs text-muted-foreground">
                Published — edits apply to reports created from the next saved version.
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {tpl.status === 'DRAFT' ? (
            <Button
              onClick={onPublish}
              disabled={publish.isPending || orderedSections.length === 0}
              title={
                orderedSections.length === 0 ? 'Add at least one section before publishing' : ''
              }
            >
              {publish.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Send className="size-4" />
              )}
              Publish
            </Button>
          ) : null}
          <Button variant="outline" onClick={onClone} disabled={clone.isPending}>
            {clone.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Copy className="size-4" />
            )}
            Clone
          </Button>
          {tpl.status !== 'ARCHIVED' ? (
            <Button variant="outline" onClick={onArchive} disabled={archive.isPending}>
              {archive.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Archive className="size-4" />
              )}
              Archive
            </Button>
          ) : null}
        </div>
      </header>

      <ReportDocEditorContext.Provider value={env}>
        <div className="flex items-start gap-6">
          <aside className="hidden w-56 shrink-0 lg:block">
            <div className="sticky top-4">
              <SectionSidebar
                sections={orderedSections.map((s) => ({ id: s.id, title: s.title }))}
                statuses={statuses}
                disabled={!contentEditable}
                activeSectionId={activeSectionId}
                onSelect={onSelectSection}
                onAdd={onAddSection}
                onRename={onRenameSection}
                onDelete={onDeleteSection}
                onReorder={(ids) => reorderSections.mutate(ids)}
              />
            </div>
          </aside>

          <div className="min-w-0 flex-1 space-y-6">
            {orderedSections.map((section) => (
              <section
                key={section.id}
                id={sectionAnchorId(section.id)}
                className="scroll-mt-4 rounded-xl border"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5">
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold">{section.title}</h2>
                    {section.subtitle ? (
                      <p className="text-xs text-muted-foreground">{section.subtitle}</p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-4">
                    <SectionFlag
                      label="Customer visible"
                      checked={section.customerVisible ?? true}
                      disabled={!contentEditable}
                      onChange={(v) => void onSectionFlagChange(section.id, 'customerVisible', v)}
                    />
                    <SectionFlag
                      label="Page break after"
                      checked={section.pageBreakAfter ?? false}
                      disabled={!contentEditable}
                      onChange={(v) => void onSectionFlagChange(section.id, 'pageBreakAfter', v)}
                    />
                    <SectionFlag
                      label="Hide when empty"
                      checked={section.hiddenWhenEmpty ?? false}
                      disabled={!contentEditable}
                      onChange={(v) => void onSectionFlagChange(section.id, 'hiddenWhenEmpty', v)}
                    />
                  </div>
                </div>
                <ReportDocEditor
                  sectionId={section.id}
                  initialContent={section.content}
                  onSaveContent={(content) =>
                    updateSection.mutateAsync({ sectionId: section.id, content })
                  }
                  onStatusChange={(status) =>
                    setStatuses((prev) =>
                      prev[section.id] === status ? prev : { ...prev, [section.id]: status },
                    )
                  }
                  onRegisterFlush={(flush) => {
                    if (flush) flushersRef.current.set(section.id, flush);
                    else flushersRef.current.delete(section.id);
                  }}
                  onFocusSection={() => setActiveSectionId(section.id)}
                  placeholder="Author the template content — techs fill in photos on the report…"
                />
              </section>
            ))}
            {orderedSections.length === 0 ? (
              <div className="rounded-xl border border-dashed p-10 text-center text-sm text-muted-foreground">
                This template has no sections yet.
                {contentEditable ? ' Use "Add section" in the sidebar to start authoring.' : ''}
              </div>
            ) : null}
          </div>

          <aside className="hidden w-80 shrink-0 xl:block">
            <div className="sticky top-4 space-y-4">
              <TemplateSettingsPane
                tpl={tpl}
                workflowEditable={contentEditable}
                metaEditable={metaEditable}
                onPatch={patch}
              />
            </div>
          </aside>
        </div>

        {/* Settings pane fallback below the editor on narrower screens. */}
        <div className="mt-6 space-y-4 xl:hidden">
          <TemplateSettingsPane
            tpl={tpl}
            workflowEditable={contentEditable}
            metaEditable={metaEditable}
            onPatch={patch}
          />
        </div>
      </ReportDocEditorContext.Provider>
    </div>
  );
}

function SectionFlag({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
      <Checkbox
        checked={checked}
        disabled={disabled}
        onCheckedChange={(v) => onChange(Boolean(v))}
      />
      {label}
    </label>
  );
}

// ─── Settings pane (§9 resolved decisions: two workflow toggles) ───────────

function TemplateSettingsPane({
  tpl,
  workflowEditable,
  metaEditable,
  onPatch,
}: {
  tpl: ReportTemplateSummary;
  workflowEditable: boolean;
  metaEditable: boolean;
  onPatch: (input: UpdateReportTemplateInput) => Promise<void>;
}) {
  const stateFlow = tpl.stateFlow ?? {};
  const requiresReview = Boolean(stateFlow.requiresReview);
  const signatureRequired = tpl.signatureConfig != null;
  const signatureLabel = tpl.signatureConfig?.customerSignatureLabel ?? DEFAULT_SIGNATURE_LABEL;

  const setRequiresReview = (v: boolean) => {
    void onPatch({ stateFlow: { ...stateFlow, requiresReview: v } });
  };

  const setSignatureRequired = (v: boolean) => {
    void onPatch({
      stateFlow: { ...stateFlow, requiresSignature: v },
      signatureConfig: v
        ? {
            ...(tpl.signatureConfig ?? {}),
            signerTypes: ['CUSTOMER'],
            customerSignatureLabel: signatureLabel,
            lockOnSignature: true,
            captureMethod: 'drawing_pad',
          }
        : null,
    });
  };

  const setSignatureLabel = (label: string) => {
    if (!tpl.signatureConfig || label === signatureLabel) return;
    void onPatch({
      signatureConfig: { ...tpl.signatureConfig, customerSignatureLabel: label },
    });
  };

  return (
    <>
      <div className="rounded-xl border p-4">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Workflow
        </p>
        <div className="space-y-3">
          <SettingsToggle
            label="Require office review"
            description="Techs submit for review; the office approves before publish."
            checked={requiresReview}
            disabled={!workflowEditable}
            onChange={setRequiresReview}
          />
          <SettingsToggle
            label="Require customer signature"
            description="The hosted view collects a drawn signature from the customer."
            checked={signatureRequired}
            disabled={!workflowEditable}
            onChange={setSignatureRequired}
          />
          {signatureRequired ? (
            <div className="pl-1">
              <label className="mb-1 block text-xs font-medium">Signature label</label>
              <Input
                key={`${tpl.id}-sig-label`}
                defaultValue={signatureLabel}
                disabled={!workflowEditable}
                onBlur={(e) => setSignatureLabel(e.target.value.trim() || DEFAULT_SIGNATURE_LABEL)}
                className="h-8 text-sm"
                placeholder={DEFAULT_SIGNATURE_LABEL}
              />
            </div>
          ) : null}
        </div>
      </div>

      <div className="rounded-xl border p-4">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Estimates
        </p>
        <SettingsToggle
          label="Spawn estimate on publish"
          description="Publishing a report with recommended line items creates an estimate."
          checked={tpl.allowEstimateSpawn}
          disabled={!workflowEditable}
          onChange={(v) => void onPatch({ allowEstimateSpawn: v })}
        />
      </div>

      <div className="rounded-xl border p-4">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Customer messaging
        </p>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium">Intro</label>
            <Textarea
              key={`${tpl.id}-intro`}
              defaultValue={tpl.customerFacingIntro ?? ''}
              disabled={!metaEditable}
              rows={3}
              placeholder="Shown at the top of the customer-facing report…"
              onBlur={(e) => {
                const v = e.target.value;
                if (v !== (tpl.customerFacingIntro ?? '')) {
                  void onPatch({ customerFacingIntro: v });
                }
              }}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">Outro</label>
            <Textarea
              key={`${tpl.id}-outro`}
              defaultValue={tpl.customerFacingOutro ?? ''}
              disabled={!metaEditable}
              rows={3}
              placeholder="Shown at the bottom, above the footer…"
              onBlur={(e) => {
                const v = e.target.value;
                if (v !== (tpl.customerFacingOutro ?? '')) {
                  void onPatch({ customerFacingOutro: v });
                }
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}

function SettingsToggle({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  );
}
